import { useEffect, useState, useCallback } from "react";
import {
  Search,
  Download,
  LogOut,
  Package,
  Mail,
  TrendingUp,
  RefreshCw,
  X,
  Truck,
  ExternalLink,
  Copy,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminGetToken,
  adminSetToken,
  adminLogin,
  adminStats,
  adminListOrders,
  adminOrderDetail,
  adminMarkShipped,
  adminDownloadCsv,
  adminDownloadInvoice,
  adminSendInvoiceEmail,
  adminMarkDelivered,
  adminCancelReviewEmail,
  adminListSubscribers,
} from "@/lib/api";

const C = {
  bg: "#FFFFFF",
  text: "#3D2B1F",
  accent: "#8B6343",
  soft: "#F5EFE6",
  muted: "#8a8378",
  border: "#dcd7ce",
};

// Build the plain-text block ready to paste into the CJ Dropshipping order form.
const buildCjCopyText = (order) => {
  if (!order) return "";
  const addr = order.shipping_address || {};
  const items = order.items_full || order.items || [];
  const itemLines = items.map((it) => {
    const name = it.product_name || it.product_id;
    const variant = it.variant_name || it.variant_id;
    const qty = it.quantity || 1;
    return `- ${name} (${variant}) × ${qty}`;
  }).join("\n");
  return [
    `Commande : ${order.order_number || ""}`,
    "",
    "DESTINATAIRE",
    `Nom : ${addr.name || "—"}`,
    `Email : ${order.email || "—"}`,
    `Téléphone : ${order.phone || "—"}`,
    "",
    "ADRESSE DE LIVRAISON",
    `${addr.line1 || ""}`,
    ...(addr.line2 ? [addr.line2] : []),
    `${addr.postal_code || ""} ${addr.city || ""}`.trim(),
    `Pays : ${addr.country || ""}`,
    "",
    "ARTICLES À EXPÉDIER",
    itemLines,
    "",
    `Total client : ${Number(order.amount_total || 0).toFixed(2)} ${order.currency || "EUR"}`,
  ].join("\n");
};

const copyTextToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (_e) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch (_ee) { /* noop */ }
    document.body.removeChild(ta);
    return ok;
  }
};

// ---------------- LOGIN ----------------
const Login = ({ onLogged }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      await adminLogin(pw);
      onLogged();
    } catch (_err) {
      toast.error("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div
      data-testid="admin-login"
      style={{ background: C.bg, color: C.text }}
      className="min-h-screen flex items-center justify-center px-6"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm"
        style={{ fontFamily: "Georgia,serif" }}
      >
        <div
          className="text-center mb-10"
          style={{ letterSpacing: ".18em", color: C.accent, fontSize: 12, textTransform: "uppercase" }}
        >
          myFLOVEA · Admin
        </div>
        <h1
          className="text-3xl mb-2 text-center"
          style={{ color: C.text }}
        >
          Connexion
        </h1>
        <p
          className="text-center text-sm mb-10"
          style={{ color: C.muted, fontFamily: "Arial,Helvetica,sans-serif" }}
        >
          Accès réservé · myFLOVEA management
        </p>
        <label
          className="block text-xs mb-2"
          style={{ color: C.accent, letterSpacing: ".18em", textTransform: "uppercase", fontFamily: "Arial" }}
        >
          Mot de passe
        </label>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          data-testid="admin-password-input"
          placeholder="••••••••"
          className="w-full px-4 py-3 outline-none mb-6"
          style={{
            background: "transparent",
            border: `1px solid ${C.border}`,
            color: C.text,
            fontSize: 15,
            borderRadius: 4,
            fontFamily: "Arial",
          }}
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full py-4 disabled:opacity-60"
          style={{
            background: C.text,
            color: "#FFFFFF",
            letterSpacing: ".22em",
            textTransform: "uppercase",
            fontSize: 11,
            borderRadius: 6,
            fontFamily: "Arial",
          }}
        >
          {loading ? "Connexion…" : "Se connecter"}
        </button>
      </form>
    </div>
  );
};

// ---------------- STAT CARD ----------------
const StatCard = ({ label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="p-5"
    style={{ background: C.soft, borderRadius: 6, border: `1px solid ${C.border}` }}
  >
    <div
      style={{ color: C.accent, letterSpacing: ".16em", fontSize: 10, textTransform: "uppercase", fontFamily: "Arial" }}
    >
      {label}
    </div>
    <div
      className="mt-2"
      style={{ color: C.text, fontFamily: "Georgia,serif", fontSize: 28, lineHeight: 1.1 }}
    >
      {value}
    </div>
    {sub && (
      <div className="mt-1" style={{ color: C.muted, fontSize: 11, fontFamily: "Arial" }}>
        {sub}
      </div>
    )}
  </div>
);

// ---------------- ORDER DRAWER ----------------
const OrderDrawer = ({ orderNumber, onClose, onShipped }) => {
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [sending, setSending] = useState(false);

  const onRefresh = useCallback(() => {
    if (!orderNumber) return;
    adminOrderDetail(orderNumber).then(setOrder).catch(() => toast.error("Commande introuvable"));
  }, [orderNumber]);

  useEffect(() => {
    onRefresh();
  }, [onRefresh]);

  const submitShipped = async (e) => {
    e.preventDefault();
    if (!tracking) {
      toast.error("Numéro de suivi requis");
      return;
    }
    setSending(true);
    try {
      await adminMarkShipped(orderNumber, {
        tracking_number: tracking,
        carrier: carrier || null,
        tracking_url: trackingUrl || null,
      });
      toast.success("Email d'expédition envoyé ✓");
      onShipped?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  if (!orderNumber) return null;
  const addr = order?.shipping_address || {};
  const items = order?.items_full || [];
  const isShipped = order?.status === "shipped";

  const copyForCJ = async () => {
    if (!order) return;
    const ok = await copyTextToClipboard(buildCjCopyText(order));
    if (ok) toast.success("Adresse copiée — prête pour CJ Dropshipping");
    else toast.error("Impossible de copier");
  };

  return (
    <div
      data-testid="admin-order-drawer"
      className="fixed inset-0 z-50 flex justify-end"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="absolute inset-0"
        style={{ background: "rgba(26,26,26,0.45)" }}
      />
      <div
        className="relative h-full overflow-y-auto w-full sm:max-w-md md:max-w-lg shadow-2xl"
        style={{ background: C.bg, color: C.text, fontFamily: "Arial,Helvetica,sans-serif" }}
      >
        <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ color: C.accent, fontSize: 10, letterSpacing: ".18em", textTransform: "uppercase" }}>
              Commande
            </div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: 22, color: C.text }}>{orderNumber}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-drawer-close"
            className="p-2"
            style={{ color: C.text }}
          >
            <X size={20} />
          </button>
        </div>

        {!order ? (
          <div className="p-6 text-sm" style={{ color: C.muted }}>Chargement…</div>
        ) : (
          <div className="p-6 space-y-7">
            {/* Status badges */}
            <div className="flex gap-2 flex-wrap">
              <span style={{ background: C.soft, color: C.text, fontSize: 11, padding: "5px 10px", borderRadius: 999, letterSpacing: ".1em", textTransform: "uppercase" }}>
                {order.status || "—"}
              </span>
              <span style={{ background: order.payment_status === "paid" ? "#2e6c4c" : C.muted, color: "#fff", fontSize: 11, padding: "5px 10px", borderRadius: 999, letterSpacing: ".1em", textTransform: "uppercase" }}>
                {order.payment_status || "—"}
              </span>
              <span style={{ color: C.muted, fontSize: 12, alignSelf: "center" }}>
                {order.created_at?.slice(0, 19).replace("T", " ")}
              </span>
            </div>

            {/* Customer */}
            <div>
              <div className="text-xs mb-2" style={{ color: C.accent, letterSpacing: ".18em", textTransform: "uppercase" }}>Client</div>
              <div style={{ fontSize: 15, lineHeight: 1.6 }}>
                <strong>{addr.name || "—"}</strong><br />
                {order.email}
                {order.phone && (
                  <>
                    <br />
                    <a
                      href={`tel:${order.phone}`}
                      data-testid="admin-order-phone"
                      style={{ color: C.text, textDecoration: "underline" }}
                    >
                      📞 {order.phone}
                    </a>
                  </>
                )}
              </div>
            </div>

            {/* Address */}
            <div>
              <div className="text-xs mb-2" style={{ color: C.accent, letterSpacing: ".18em", textTransform: "uppercase" }}>Adresse de livraison</div>
              <div style={{ fontSize: 15, lineHeight: 1.6 }}>
                {addr.line1 || "—"}
                {addr.line2 && <><br />{addr.line2}</>}
                <br />
                {addr.postal_code} {addr.city}<br />
                <strong>{addr.country}</strong>
              </div>
              <button
                type="button"
                onClick={copyForCJ}
                data-testid="admin-copy-cj"
                className="mt-3 inline-flex items-center gap-2 px-3 py-2"
                style={{
                  background: "#FFFFFF",
                  border: `1px solid ${C.accent}`,
                  color: C.text,
                  borderRadius: 4,
                  fontSize: 11,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  fontFamily: "Arial",
                }}
              >
                <Copy size={13} />
                Copier adresse pour CJ
              </button>
              {order.invoice_number && (
                <button
                  type="button"
                  data-testid="admin-download-invoice"
                  onClick={async () => {
                    try {
                      const blob = await adminDownloadInvoice(order.order_number);
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${order.invoice_number}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (e) {
                      toast.error("Impossible de télécharger la facture");
                    }
                  }}
                  className="mt-2 ml-2 inline-flex items-center gap-2 px-3 py-2"
                  style={{
                    background: C.accent,
                    border: `1px solid ${C.accent}`,
                    color: "#FFFFFF",
                    borderRadius: 4,
                    fontSize: 11,
                    letterSpacing: ".18em",
                    textTransform: "uppercase",
                    fontFamily: "Arial",
                  }}
                >
                  📄 Facture {order.invoice_number}
                </button>
              )}
              {order.payment_status === "paid" && (
                <button
                  type="button"
                  data-testid="admin-email-invoice"
                  onClick={async () => {
                    try {
                      const res = await adminSendInvoiceEmail(order.order_number);
                      toast.success(`Facture ${res.invoice_number} envoyée à ${res.sent_to}`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Envoi impossible");
                    }
                  }}
                  className="mt-2 ml-2 inline-flex items-center gap-2 px-3 py-2"
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${C.accent}`,
                    color: C.text,
                    borderRadius: 4,
                    fontSize: 11,
                    letterSpacing: ".18em",
                    textTransform: "uppercase",
                    fontFamily: "Arial",
                  }}
                >
                  📧 Envoyer facture au client
                </button>
              )}
              {order.payment_status === "paid" && !order.delivered_at && (
                <button
                  type="button"
                  data-testid="admin-mark-delivered"
                  onClick={async () => {
                    if (!window.confirm("Confirmer que le colis a bien été livré au client ? Cela déclenchera l'email de demande d'avis 10 jours plus tard.")) return;
                    try {
                      const res = await adminMarkDelivered(order.order_number);
                      toast.success(res.already_delivered
                        ? "Commande déjà marquée comme livrée"
                        : "Livraison confirmée. Email d'avis prévu dans 10 jours.");
                      onRefresh && onRefresh();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Erreur");
                    }
                  }}
                  className="mt-2 ml-2 inline-flex items-center gap-2 px-3 py-2"
                  style={{
                    background: "#2f6b3f",
                    border: "1px solid #2f6b3f",
                    color: "#FFFFFF",
                    borderRadius: 4,
                    fontSize: 11,
                    letterSpacing: ".18em",
                    textTransform: "uppercase",
                    fontFamily: "Arial",
                  }}
                >
                  ✅ Marquer comme livrée
                </button>
              )}
              {order.delivered_at && (
                <span
                  data-testid="admin-delivered-badge"
                  className="mt-2 ml-2 inline-flex items-center gap-1 px-3 py-2"
                  style={{ fontSize: 11, color: C.text, letterSpacing: ".14em", textTransform: "uppercase", fontFamily: "Arial" }}
                >
                  ✅ Livrée le {String(order.delivered_at).slice(0, 10)}
                </span>
              )}
              {(order.scheduled_review_email_ids || []).length > 0 && (
                <button
                  type="button"
                  data-testid="admin-cancel-review-email"
                  onClick={async () => {
                    if (!window.confirm("Annuler le(s) email(s) de demande d'avis déjà planifié(s) pour cette commande ?")) return;
                    try {
                      const res = await adminCancelReviewEmail(order.order_number);
                      toast.success(`${res.cancelled_count} email(s) d'avis annulé(s)`);
                      onRefresh && onRefresh();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Erreur");
                    }
                  }}
                  className="mt-2 ml-2 inline-flex items-center gap-2 px-3 py-2"
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #a33",
                    color: "#a33",
                    borderRadius: 4,
                    fontSize: 11,
                    letterSpacing: ".18em",
                    textTransform: "uppercase",
                    fontFamily: "Arial",
                  }}
                >
                  ✖ Annuler email d&apos;avis
                </button>
              )}
            </div>

            {/* Items */}
            <div>
              <div className="text-xs mb-3" style={{ color: C.accent, letterSpacing: ".18em", textTransform: "uppercase" }}>Articles</div>
              <table className="w-full" style={{ fontSize: 14 }}>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td className="py-2">
                        <div style={{ color: C.text }}>{it.product_name}</div>
                        <div style={{ color: C.muted, fontSize: 12 }}>{it.variant_name}</div>
                      </td>
                      <td className="py-2 text-right" style={{ color: C.muted }}>× {it.quantity}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="pt-4 font-semibold" style={{ color: C.text }}>Total payé</td>
                    <td className="pt-4 text-right" style={{ fontFamily: "Georgia,serif", fontSize: 18 }}>
                      {Number(order.amount_total || 0).toFixed(2)} {order.currency || "EUR"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tracking already set? */}
            {order.tracking?.number && (
              <div className="p-4" style={{ background: C.soft, borderRadius: 6, borderLeft: `3px solid ${C.accent}` }}>
                <div className="text-xs mb-2" style={{ color: C.accent, letterSpacing: ".18em", textTransform: "uppercase" }}>Suivi</div>
                <div style={{ fontSize: 14, color: C.text }}>
                  <strong>{order.tracking.number}</strong>
                  {order.tracking.carrier && ` · ${order.tracking.carrier}`}
                </div>
                {order.tracking.url && (
                  <a href={order.tracking.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs" style={{ color: C.accent, textDecoration: "underline" }}>
                    <ExternalLink size={12} /> Suivre
                  </a>
                )}
              </div>
            )}

            {/* Mark shipped form */}
            {!isShipped && (
              <form
                onSubmit={submitShipped}
                data-testid="admin-shipped-form"
                className="p-4 space-y-3"
                style={{ background: C.soft, borderRadius: 6 }}
              >
                <div className="text-xs" style={{ color: C.accent, letterSpacing: ".18em", textTransform: "uppercase" }}>
                  Marquer comme expédié
                </div>
                <input
                  type="text"
                  placeholder="N° de suivi *"
                  required
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  data-testid="admin-shipped-tracking"
                  className="w-full px-3 py-2"
                  style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 14 }}
                />
                <input
                  type="text"
                  placeholder="Transporteur (Colissimo, DPD…)"
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="w-full px-3 py-2"
                  style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 14 }}
                />
                <input
                  type="url"
                  placeholder="URL de suivi (optionnel)"
                  value={trackingUrl}
                  onChange={(e) => setTrackingUrl(e.target.value)}
                  className="w-full px-3 py-2"
                  style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 14 }}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="admin-shipped-submit"
                  className="w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: C.text, color: "#fff", borderRadius: 6, fontSize: 11, letterSpacing: ".22em", textTransform: "uppercase" }}
                >
                  <Truck size={14} />
                  {sending ? "Envoi…" : "Notifier le client"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- DASHBOARD ----------------
const Dashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState({ items: [], total: 0 });
  const [subs, setSubs] = useState({ items: [], total: 0 });
  const [tab, setTab] = useState("orders");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawerOrder, setDrawerOrder] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, sub] = await Promise.all([
        adminStats(),
        adminListOrders({ status, search, limit: 200 }),
        adminListSubscribers({ limit: 200 }),
      ]);
      setStats(s);
      setOrders(o);
      setSubs(sub);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminSetToken("");
        onLogout();
        return;
      }
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [status, search, onLogout]);

  useEffect(() => { refresh(); }, [refresh]);

  const downloadCsv = async () => {
    try {
      const blob = await adminDownloadCsv(status);
      const url = window.URL.createObjectURL(new Blob([blob], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `myflovea-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (_err) {
      toast.error("Erreur export CSV");
    }
  };

  const handleLogout = () => {
    adminSetToken("");
    onLogout();
  };

  return (
    <div style={{ background: C.bg, color: C.text, minHeight: "100vh", fontFamily: "Arial,Helvetica,sans-serif" }}>
      {/* Header */}
      <header className="px-4 sm:px-8 py-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div>
          <div style={{ color: C.accent, fontSize: 10, letterSpacing: ".22em", textTransform: "uppercase" }}>myFLOVEA</div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: 22, color: C.text }}>Tableau de bord</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refresh}
            data-testid="admin-refresh"
            className="p-2 sm:p-3"
            style={{ border: `1px solid ${C.border}`, borderRadius: 4, color: C.text }}
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="px-3 sm:px-4 py-2 sm:py-3 inline-flex items-center gap-2"
            style={{ border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase" }}
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </header>

      <main className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1280px] mx-auto">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <StatCard testid="stat-orders" label="Commandes" value={stats.total_orders} sub={`${stats.recent_orders_7d} sur 7j`} />
            <StatCard testid="stat-revenue" label="CA total" value={`${stats.revenue_total.toFixed(2)} €`} sub={`${stats.revenue_7d.toFixed(2)} € sur 7j`} />
            <StatCard testid="stat-pending" label="À expédier" value={stats.pending_shipment} sub={`${stats.shipped_orders} expédiées`} />
            <StatCard testid="stat-subs" label="Newsletter" value={stats.newsletter_subscribers} sub="abonnés" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-5" style={{ borderBottom: `1px solid ${C.border}` }}>
          {[
            { id: "orders", label: "Commandes", icon: Package },
            { id: "subs", label: "Newsletter", icon: Mail },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className="px-4 py-3 inline-flex items-center gap-2"
                style={{
                  borderBottom: `2px solid ${active ? C.text : "transparent"}`,
                  color: active ? C.text : C.muted,
                  fontSize: 13,
                  letterSpacing: ".06em",
                  marginBottom: -1,
                }}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ORDERS TAB */}
        {tab === "orders" && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-5">
              <div className="flex-1 relative">
                <Search size={14} className="absolute top-1/2 left-3 -translate-y-1/2" style={{ color: C.muted }} />
                <input
                  type="text"
                  placeholder="Rechercher (nom, email, n°, ville…)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="admin-search"
                  className="w-full pl-9 pr-3 py-2.5"
                  style={{ border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 14, color: C.text, background: "#fff" }}
                />
              </div>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="admin-status-filter"
                className="px-3 py-2.5"
                style={{ border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 14, color: C.text, background: "#fff" }}
              >
                <option value="all">Toutes</option>
                <option value="confirmed">Confirmées</option>
                <option value="pending_shipment">À expédier</option>
                <option value="shipped">Expédiées</option>
              </select>
              <button
                onClick={downloadCsv}
                data-testid="admin-export-csv"
                className="px-4 py-2.5 inline-flex items-center justify-center gap-2"
                style={{ background: C.text, color: "#fff", borderRadius: 4, fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase" }}
              >
                <Download size={14} />
                Export CSV
              </button>
            </div>

            {/* Orders list */}
            <div className="overflow-x-auto" style={{ border: `1px solid ${C.border}`, borderRadius: 6 }}>
              <table className="w-full" style={{ fontSize: 13 }}>
                <thead style={{ background: C.soft, color: C.accent, fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase" }}>
                  <tr>
                    <th className="px-3 py-3 text-left">Commande</th>
                    <th className="px-3 py-3 text-left">Client</th>
                    <th className="px-3 py-3 text-left hidden md:table-cell">Articles</th>
                    <th className="px-3 py-3 text-right">Montant</th>
                    <th className="px-3 py-3 text-left hidden sm:table-cell">Statut</th>
                    <th className="px-3 py-3 text-left hidden lg:table-cell">Date</th>
                    <th className="px-3 py-3 text-center" style={{ width: 56 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.items.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-8 text-center" style={{ color: C.muted }}>Aucune commande</td></tr>
                  ) : (
                    orders.items.map((o) => {
                      const isShipped = o.status === "shipped";
                      const onCopyRow = async (e) => {
                        e.stopPropagation();
                        const ok = await copyTextToClipboard(buildCjCopyText(o));
                        if (ok) toast.success(`${o.order_number} → copié pour CJ`);
                        else toast.error("Impossible de copier");
                      };
                      return (
                      <tr
                        key={o.order_number}
                        onClick={() => setDrawerOrder(o.order_number)}
                        data-testid={`admin-order-row-${o.order_number}`}
                        className="cursor-pointer hover:opacity-90"
                        style={{ borderTop: `1px solid ${C.border}` }}
                      >
                        <td className="px-3 py-3" style={{ fontFamily: "Georgia,serif", color: C.text }}>{o.order_number}</td>
                        <td className="px-3 py-3" style={{ color: C.text }}>
                          <div>{o.shipping_address?.name || "—"}</div>
                          <div style={{ color: C.muted, fontSize: 11 }}>{o.email}</div>
                        </td>
                        <td className="px-3 py-3 hidden md:table-cell" style={{ color: C.muted, fontSize: 12 }}>
                          {(o.items_full || []).map((it) => `${it.product_name} (${it.variant_name}) × ${it.quantity}`).join(" · ")}
                        </td>
                        <td className="px-3 py-3 text-right" style={{ fontFamily: "Georgia,serif", color: C.text }}>
                          {Number(o.amount_total || 0).toFixed(2)} {o.currency || "EUR"}
                        </td>
                        <td className="px-3 py-3 hidden sm:table-cell">
                          <span style={{ background: isShipped ? "#2e6c4c" : C.soft, color: isShipped ? "#fff" : C.text, fontSize: 10, padding: "3px 8px", borderRadius: 999, letterSpacing: ".08em", textTransform: "uppercase" }}>
                            {o.status || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-3 hidden lg:table-cell" style={{ color: C.muted, fontSize: 12 }}>
                          {o.created_at?.slice(0, 10)}
                        </td>
                        <td className="px-2 py-3 text-center">
                          <button
                            type="button"
                            onClick={onCopyRow}
                            data-testid={`admin-quick-copy-${o.order_number}`}
                            title="Copier l'adresse pour CJ Dropshipping"
                            aria-label="Copier l'adresse pour CJ Dropshipping"
                            className="inline-flex items-center justify-center"
                            style={{
                              width: 34, height: 34, borderRadius: 6,
                              background: isShipped ? "#FFFFFF" : C.soft,
                              border: `1px solid ${isShipped ? C.border : C.accent}`,
                              color: isShipped ? C.muted : C.text,
                              opacity: isShipped ? 0.55 : 1,
                            }}
                          >
                            <Zap size={15} strokeWidth={1.8} />
                          </button>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-xs" style={{ color: C.muted }}>
              {orders.total} commande{orders.total > 1 ? "s" : ""} · cliquez sur une ligne pour voir le détail / marquer comme expédiée
            </div>
          </>
        )}

        {/* SUBSCRIBERS TAB */}
        {tab === "subs" && (
          <div className="overflow-x-auto" style={{ border: `1px solid ${C.border}`, borderRadius: 6 }}>
            <table className="w-full" style={{ fontSize: 13 }}>
              <thead style={{ background: C.soft, color: C.accent, fontSize: 10, letterSpacing: ".16em", textTransform: "uppercase" }}>
                <tr>
                  <th className="px-3 py-3 text-left">Email</th>
                  <th className="px-3 py-3 text-left hidden sm:table-cell">Langue</th>
                  <th className="px-3 py-3 text-left hidden md:table-cell">Source</th>
                  <th className="px-3 py-3 text-left">Inscrit le</th>
                </tr>
              </thead>
              <tbody>
                {subs.items.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-8 text-center" style={{ color: C.muted }}>Aucun abonné</td></tr>
                ) : (
                  subs.items.map((s, i) => (
                    <tr key={s.email + i} data-testid={`admin-sub-row-${i}`} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td className="px-3 py-3" style={{ color: C.text }}>{s.email}</td>
                      <td className="px-3 py-3 hidden sm:table-cell" style={{ color: C.muted }}>{(s.lang || "fr").toUpperCase()}</td>
                      <td className="px-3 py-3 hidden md:table-cell">
                        <span style={{ background: C.soft, color: C.text, fontSize: 10, padding: "3px 8px", borderRadius: 999, textTransform: "uppercase" }}>
                          {s.source || "newsletter"}
                        </span>
                      </td>
                      <td className="px-3 py-3" style={{ color: C.muted, fontSize: 12 }}>{s.subscribed_at?.slice(0, 10)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Drawer */}
      <OrderDrawer
        orderNumber={drawerOrder}
        onClose={() => setDrawerOrder(null)}
        onShipped={refresh}
      />
    </div>
  );
};

// ---------------- ROOT ----------------
const Admin = () => {
  const [authed, setAuthed] = useState(!!adminGetToken());

  // Verify token on mount: try stats call
  useEffect(() => {
    if (!authed) return;
    adminStats().catch((err) => {
      if (err?.response?.status === 401) {
        adminSetToken("");
        setAuthed(false);
      }
    });
  }, [authed]);

  if (!authed) return <Login onLogged={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
};

export default Admin;
